/**
 * lib/analysisPdf.ts
 * ------------------------------------------------------------------
 * PDF builders for the analysis screens, on jspdf-autotable + the
 * shared LKC chrome (lib/pdfChrome):
 *
 *   buildMarksheetPdf       — student × subject matrix (Student Analysis;
 *                             full marksheet or a department subset).
 *   buildTeacherAnalysisPdf — per-form (teacher × subject) comparison,
 *                             grouped by subject (Teacher Analysis).
 *
 * Both share the branded letterhead/footer so every analysis export
 * matches without copy-paste.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, {
  type CellHookData,
  type RowInput,
  type Styles,
} from 'jspdf-autotable';
import {
  PDF,
  CONTENT_TOP,
  pageGeom,
  drawLetterhead,
  stampFooters,
} from '@/lib/pdfChrome';

/* ==================================================================
   1. MARKSHEET  (Student Analysis)
   ================================================================== */

export interface MarksheetPdfSubject {
  id: string;
  name: string;
}

export interface MarksheetPdfRow {
  rank: number;
  name: string;
  className?: string;
  marks: Record<string, number | null>;
  average: number | null;
  pass: number;
  fail: number;
}

export interface MarksheetPdfData {
  title: string;
  metaLine?: string;
  subjects: MarksheetPdfSubject[];
  rows: MarksheetPdfRow[];
  subjectAverages: Record<string, number>;
  subjectMedians?: Record<string, number>;
  classAverage: number | null;
  classMedian?: number | null;
  passMark: number;
  showClassCol?: boolean;
  logoDataUrl?: string | null;
  generatedBy?: string;
}

/** Pass/fail tint for a numeric cell, mirroring the on-screen scale. */
function markFill(v: number | null, passMark: number): PDFTuple | undefined {
  if (v === null || Number.isNaN(v)) return undefined;
  if (v >= passMark + 25) return [209, 240, 224]; // strong green
  if (v >= passMark) return [226, 244, 235]; // light green
  if (v >= passMark - 10) return [255, 243, 215]; // amber
  return [253, 226, 226]; // red
}
type PDFTuple = [number, number, number];

export function buildMarksheetPdf(data: MarksheetPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

  const showClass = !!data.showClassCol;
  // 'No.' = sequential position (last row = total students). 'Rank' = dense
  // competition rank (tied averages share a number), so the two never get
  // confused for each other — the whole point of this sheet's earlier bug.
  const head = [
    'No.',
    'Student',
    ...(showClass ? ['Class'] : []),
    'Rank',
    ...data.subjects.map((s) => (s.name.length > 9 ? s.name.slice(0, 8) + '…' : s.name)),
    'Avg',
    'P',
    'F',
  ];
  const rankCol = showClass ? 3 : 2;
  const firstSubjectCol = rankCol + 1;
  const avgCol = firstSubjectCol + data.subjects.length;
  // numeric columns that get a pass/fail tint: each subject + the Avg col
  const numericCols = new Set<number>();
  for (let i = 0; i < data.subjects.length; i++) numericCols.add(firstSubjectCol + i);
  numericCols.add(avgCol);

  const body: RowInput[] = data.rows.map((r, i) => [
    String(i + 1),
    r.name,
    ...(showClass ? [r.className ?? ''] : []),
    r.rank ? String(r.rank) : '—',
    ...data.subjects.map((s) => {
      const m = r.marks[s.id];
      return m === null || m === undefined ? '–' : String(m);
    }),
    r.average === null ? '—' : String(r.average),
    r.pass ? String(r.pass) : '—',
    r.fail ? String(r.fail) : '—',
  ]);

  const foot: RowInput[] = [
    [
      '',
      `AVERAGE  (${data.rows.length} students)`,
      ...(showClass ? [''] : []),
      '',
      ...data.subjects.map((s) =>
        data.subjectAverages[s.id] === undefined ? '—' : String(data.subjectAverages[s.id]),
      ),
      data.classAverage === null ? '—' : String(data.classAverage),
      '',
      '',
    ],
  ];
  if (data.subjectMedians) {
    foot.push([
      '',
      'MEDIAN',
      ...(showClass ? [''] : []),
      '',
      ...data.subjects.map((s) =>
        data.subjectMedians![s.id] === undefined ? '—' : String(data.subjectMedians![s.id]),
      ),
      data.classMedian == null ? '—' : String(data.classMedian),
      '',
      '',
    ]);
  }

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { cellWidth: 9, halign: 'center', textColor: PDF.MUTED },
    1: { cellWidth: 40, halign: 'left', fontStyle: 'bold' },
  };
  if (showClass) columnStyles[2] = { cellWidth: 18, halign: 'left', textColor: PDF.MUTED };
  columnStyles[rankCol] = { cellWidth: 9, halign: 'center', textColor: PDF.MUTED };
  for (let i = 0; i < data.subjects.length; i++)
    columnStyles[firstSubjectCol + i] = { halign: 'center' };
  columnStyles[avgCol] = { cellWidth: 12, halign: 'center', fontStyle: 'bold' };
  columnStyles[avgCol + 1] = { cellWidth: 8, halign: 'center', textColor: [5, 150, 105] };
  columnStyles[avgCol + 2] = { cellWidth: 8, halign: 'center', textColor: [220, 38, 38] };

  autoTable(pdf, {
    head: [head],
    body,
    foot,
    showFoot: 'lastPage',
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 7,
      textColor: PDF.INK,
      cellPadding: { top: 1.4, right: 1.5, bottom: 1.4, left: 1.5 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'ellipsize',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold',
      fontSize: 6.8,
      halign: 'center',
      lineWidth: 0,
    },
    footStyles: {
      fillColor: PDF.BAND,
      textColor: PDF.NAVY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'center',
      lineWidth: { top: 0.4, bottom: 0, left: 0, right: 0 },
    },
    columnStyles,
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: data.title,
        metaLine: data.metaLine,
        logoDataUrl: null,
        geom,
      });
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body') return;
      if (!numericCols.has(h.column.index)) return;
      const raw = Array.isArray(h.cell.text) ? h.cell.text[0] : h.cell.text;
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      const fill = markFill(n, data.passMark);
      if (fill) h.cell.styles.fillColor = fill;
      // Failing marks stand out: red + bold (§12).
      if (n < data.passMark) {
        h.cell.styles.textColor = [193, 53, 53];
        h.cell.styles.fontStyle = 'bold';
      }
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   2. TEACHER ANALYSIS  (per-form, grouped by subject)
   ================================================================== */

export interface TeacherAnalysisPdfRow {
  teacherName: string;
  n: number; // graded
  gradeCounts: Record<string, number>;
  passRate: number | null;
  average: number | null;
}

export interface TeacherAnalysisPdfGroup {
  subjectName: string;
  rows: TeacherAnalysisPdfRow[];
}

export interface TeacherAnalysisPdfData {
  title: string;
  metaLine?: string;
  grades: string[]; // e.g. ['A*','A','B','C','D','E','U']
  groups: TeacherAnalysisPdfGroup[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

type TaRowMeta = { type: 'section' } | { type: 'row' };

export function buildTeacherAnalysisPdf(data: TeacherAnalysisPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

  const head = ['Teacher', 'N', ...data.grades, 'Pass %', 'Avg'];
  const nCols = head.length;

  const body: RowInput[] = [];
  const meta: TaRowMeta[] = [];
  for (const g of data.groups) {
    body.push([
      {
        content: g.subjectName,
        colSpan: nCols,
        styles: {
          fillColor: PDF.BAND,
          textColor: PDF.NAVY,
          fontStyle: 'bold',
          fontSize: 9.5,
          halign: 'left',
          cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
        },
      },
    ]);
    meta.push({ type: 'section' });
    for (const r of g.rows) {
      body.push([
        r.teacherName,
        String(r.n),
        ...data.grades.map((gr) => (r.gradeCounts[gr] ? String(r.gradeCounts[gr]) : '·')),
        r.passRate === null ? '—' : `${r.passRate}%`,
        r.average === null ? '—' : String(r.average),
      ]);
      meta.push({ type: 'row' });
    }
  }

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { cellWidth: 'auto', halign: 'left', fontStyle: 'bold' },
    1: { cellWidth: 12, halign: 'center' },
  };
  let ci = 2;
  for (let i = 0; i < data.grades.length; i++)
    columnStyles[ci++] = { cellWidth: 12, halign: 'center' };
  columnStyles[ci++] = { cellWidth: 18, halign: 'center', fontStyle: 'bold' }; // Pass %
  columnStyles[ci++] = { cellWidth: 16, halign: 'center', fontStyle: 'bold' }; // Avg

  autoTable(pdf, {
    head: [head],
    body,
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: PDF.INK,
      cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'ellipsize',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'center',
      lineWidth: 0,
    },
    columnStyles,
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: data.title,
        metaLine: data.metaLine,
        logoDataUrl: null,
        geom,
      });
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body') return;
      const m = meta[h.row.index];
      if (!m || m.type === 'section') return;
      // Tint the Pass % column by band.
      const passCol = 2 + data.grades.length;
      if (h.column.index === passCol) {
        const raw = Array.isArray(h.cell.text) ? h.cell.text[0] : h.cell.text;
        const n = parseInt(String(raw), 10);
        if (Number.isFinite(n)) {
          h.cell.styles.fillColor =
            n >= 80 ? [209, 240, 224] : n >= 50 ? [255, 243, 215] : [253, 226, 226];
        }
      }
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   2b. HOD DEPARTMENT ANALYSIS  (Teacher-to-Teacher, per form)
   ================================================================== */

export interface HodAnalysisPdfRow {
  teacherName: string;
  classLabel: string;
  gradeCounts: Record<string, number>;
  total: number;
}
export interface HodAnalysisPdfForm { formLabel: string; rows: HodAnalysisPdfRow[]; }
export interface HodAnalysisPdfSubject { subjectName: string; forms: HodAnalysisPdfForm[]; }
export interface HodAnalysisPdfData {
  title: string;
  metaLine?: string;
  grades: string[]; // A*..U (credit = first 4, pass = first 6)
  subjects: HodAnalysisPdfSubject[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildHodAnalysisPdf(data: HodAnalysisPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const grades = data.grades;
  const nCols = 2 + grades.length + 3; // Teacher, Class, grades, Total, Credit%, Pass%
  const head = ['Teacher', 'Class', ...grades, 'Total', 'Credit %', 'Pass %'];

  const pct = (n: number, tot: number) => (tot > 0 ? `${Math.round((n / tot) * 100)}%` : '0%');
  const creditOf = (gc: Record<string, number>) => grades.reduce((s, g, i) => s + (i < 4 ? (gc[g] || 0) : 0), 0);
  const passOf = (gc: Record<string, number>) => grades.reduce((s, g, i) => s + (i < 6 ? (gc[g] || 0) : 0), 0);

  const columnStyles: Record<number, Partial<Styles>> = {
    0: { cellWidth: 'auto', halign: 'left', fontStyle: 'bold' },
    1: { cellWidth: 16, halign: 'left' },
  };
  let ci = 2;
  for (let i = 0; i < grades.length; i++) columnStyles[ci++] = { cellWidth: 10, halign: 'center' };
  columnStyles[ci++] = { cellWidth: 14, halign: 'center', fontStyle: 'bold' }; // Total
  columnStyles[ci++] = { cellWidth: 18, halign: 'center' }; // Credit %
  columnStyles[ci++] = { cellWidth: 16, halign: 'center' }; // Pass %

  // Draw the letterhead once per physical page (multiple tables may land on
  // the same page).
  const seenPages = new Set<number>();
  const drawHeader = () => {
    const p = pdf.getNumberOfPages();
    if (seenPages.has(p)) return;
    seenPages.add(p);
    drawLetterhead(pdf, { title: data.title, metaLine: data.metaLine, logoDataUrl: null, geom });
  };

  // ONE table per (subject × form) block, with pageBreak:'avoid' so a block
  // never splits across pages — it moves to a fresh page instead of getting
  // its rows cut off.
  let y = CONTENT_TOP;
  let drewAny = false;
  for (const subj of data.subjects) {
    for (const form of subj.forms) {
      if (!form.rows.length) continue;
      const meta: ('band' | 'row' | 'total')[] = ['band'];
      const body: RowInput[] = [[{
        // Ink-light section band (matches the Mark Entry export): a faint tint
        // with navy text instead of a solid navy fill, to save toner.
        content: `${subj.subjectName} — ${form.formLabel}`, colSpan: nCols,
        styles: { fillColor: PDF.BAND, textColor: PDF.NAVY, fontStyle: 'bold', fontSize: 9.5, halign: 'left', cellPadding: { top: 2, right: 3, bottom: 2, left: 3 } },
      }]];
      const tot: Record<string, number> = Object.fromEntries(grades.map((g) => [g, 0]));
      let totN = 0;
      for (const r of form.rows) {
        grades.forEach((g) => { tot[g] += r.gradeCounts[g] || 0; });
        totN += r.total;
        body.push([
          r.teacherName, r.classLabel,
          ...grades.map((g) => (r.gradeCounts[g] ? String(r.gradeCounts[g]) : '·')),
          String(r.total), pct(creditOf(r.gradeCounts), r.total), pct(passOf(r.gradeCounts), r.total),
        ]);
        meta.push('row');
      }
      body.push(['', 'Total', ...grades.map((g) => String(tot[g])), String(totN), pct(creditOf(tot), totN), pct(passOf(tot), totN)]);
      meta.push('total');

      autoTable(pdf, {
        head: [head], body,
        startY: y,
        margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
        pageBreak: 'avoid',   // keep this block whole — start a fresh page if needed
        theme: 'plain',
        styles: {
          font: 'helvetica', fontSize: 8, textColor: PDF.INK,
          cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
          lineColor: PDF.RULE, lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
          overflow: 'ellipsize', valign: 'middle',
        },
        headStyles: {
          // Ink-light column header: faint fill + navy text + a thin navy
          // underline (the block header repeats per table, so a solid navy
          // header on every block was the biggest toner cost).
          fillColor: PDF.BAND, textColor: PDF.NAVY, fontStyle: 'bold', fontSize: 7.5,
          halign: 'center', lineColor: PDF.NAVY, lineWidth: { bottom: 0.4, top: 0, left: 0, right: 0 },
        },
        columnStyles,
        didParseCell: (h: CellHookData) => {
          if (h.section !== 'body') return;
          const m = meta[h.row.index];
          if (m === 'total') { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [245, 247, 250]; }
          // Zebra striping on data rows for readability (as in the Mark Entry
          // export); band is row 0, data rows start at 1.
          else if (m === 'row' && h.row.index % 2 === 0) { h.cell.styles.fillColor = PDF.ZEBRA; }
        },
        didDrawPage: drawHeader,
      });
      // Next block starts just below this one (a small gap); on overflow the
      // next table's pageBreak:'avoid' resets it to a fresh page.
      const finalY = (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;
      y = finalY + 4;
      drewAny = true;
    }
  }
  if (!drewAny) drawHeader();

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   3. AT-RISK LIST  (Student Analysis)
   ================================================================== */

export interface AtRiskPdfRow {
  name: string;
  tier: 'critical' | 'borderline';
  average: number | null;
  failedSubjects: string[]; // subject names
  reason: string;
}

export interface AtRiskPdfData {
  title: string;
  metaLine?: string;
  rows: AtRiskPdfRow[];
  criticalCount: number;
  borderlineCount: number;
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildAtRiskPdf(data: AtRiskPdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const head = ['Student', 'Tier', 'Avg', 'Failed subjects', 'Reason'];
  const nCols = head.length;

  // First body row is a count banner (colSpan); the rest are data rows.
  const body: RowInput[] = [
    [
      {
        content: `Critical: ${data.criticalCount}    Borderline: ${data.borderlineCount}    Total: ${data.rows.length}`,
        colSpan: nCols,
        styles: {
          fillColor: PDF.BAND,
          textColor: PDF.NAVY,
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'left',
          cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
        },
      },
    ],
    ...data.rows.map((r) => [
      r.name,
      r.tier,
      r.average === null ? '-' : String(r.average),
      r.failedSubjects.length ? r.failedSubjects.join(', ') : '-',
      r.reason,
    ]),
  ];
  const isBanner = [true, ...data.rows.map(() => false)];

  autoTable(pdf, {
    head: [head],
    body,
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: PDF.INK,
      cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
      lineWidth: 0,
    },
    columnStyles: {
      0: { cellWidth: 44, fontStyle: 'bold' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 50 },
      4: { cellWidth: 'auto' },
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body' || isBanner[h.row.index]) return;
      if (h.column.index !== 1) return;
      const raw = Array.isArray(h.cell.text) ? h.cell.text[0] : h.cell.text;
      if (String(raw) === 'critical') {
        h.cell.styles.fillColor = [253, 226, 226];
        h.cell.styles.textColor = [153, 27, 27];
      } else if (String(raw) === 'borderline') {
        h.cell.styles.fillColor = [255, 243, 215];
        h.cell.styles.textColor = [146, 64, 14];
      }
    },
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: data.title,
        metaLine: data.metaLine,
        logoDataUrl: null,
        geom,
      });
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   4. SUMMARY  (Student Analysis — subject performance + distribution)
   ================================================================== */

export interface SummaryPdfSubjectRow {
  subject: string;
  avg: number | null;
  median?: number | null;
  passRate: number | null;
  highest: number | null;
  lowest: number | null;
}

export interface SummaryPdfBucket {
  label: string;
  count: number;
}

export interface SummaryPdfData {
  title: string;
  metaLine?: string;
  subjects: SummaryPdfSubjectRow[];
  distribution: SummaryPdfBucket[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildSummaryPdf(data: SummaryPdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const num = (v: number | null) => (v === null ? '-' : String(v));

  const chrome = {
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain' as const,
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: PDF.INK,
      cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
      lineColor: PDF.RULE,
      lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      valign: 'middle' as const,
    },
    headStyles: {
      fillColor: PDF.NAVY,
      textColor: PDF.WHITE,
      fontStyle: 'bold' as const,
      fontSize: 7.5,
      halign: 'left' as const,
      lineWidth: 0,
    },
    didDrawPage: () =>
      drawLetterhead(pdf, {
        title: data.title,
        metaLine: data.metaLine,
        logoDataUrl: null,
        geom,
      }),
  };

  // Table 1 — subject performance (Pass % tinted by band).
  autoTable(pdf, {
    ...chrome,
    head: [['Subject', 'Avg', 'Median', 'Pass %', 'Highest', 'Lowest']],
    body: data.subjects.map((s) => [
      s.subject,
      num(s.avg),
      num(s.median ?? null),
      s.passRate === null ? '-' : `${s.passRate}%`,
      num(s.highest),
      num(s.lowest),
    ]),
    startY: CONTENT_TOP,
    styles: { ...chrome.styles, overflow: 'ellipsize' },
    columnStyles: {
      0: { cellWidth: 'auto', fontStyle: 'bold' },
      1: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 22, halign: 'center' },
      4: { cellWidth: 20, halign: 'center' },
      5: { cellWidth: 20, halign: 'center' },
    },
    didParseCell: (h: CellHookData) => {
      if (h.section !== 'body' || h.column.index !== 3) return;
      const raw = Array.isArray(h.cell.text) ? h.cell.text[0] : h.cell.text;
      const n = parseInt(String(raw), 10);
      if (Number.isFinite(n)) {
        h.cell.styles.fillColor =
          n >= 80 ? [209, 240, 224] : n >= 50 ? [255, 243, 215] : [253, 226, 226];
      }
    },
  });

  const firstFinalY =
    (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ??
    CONTENT_TOP;

  // Table 2 — student-average distribution.
  autoTable(pdf, {
    ...chrome,
    head: [['Average band', 'Students']],
    body: data.distribution.map((b) => [b.label, String(b.count)]),
    startY: firstFinalY + 8,
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
